import { useState } from 'react';

import type { ProtectionValidation } from '../types/protection.types';

/**
 * ProtectionWarningBanner — Sprint Backend Protection v1 Agent C (Item 226).
 *
 * Banner inline canonical sticky acima do composer. Renderiza quando
 * `protection-agent` retornou `severity ∈ {low, medium}` + `action = 'warn'`
 * (tier Pro+/Team+ por default; configurável por tenant via
 * `protection-outbound-policy` — Item 224).
 *
 * Pattern visual canonical reaproveitado de ConflictWarningBanner (severity
 * `--high`) — atendente percebe risco mas pode prosseguir.
 *
 * Dismissible canonical:
 *   - Botão "Dispensar" (X) fecha banner.
 *   - Decisão Agent C: NÃO auto-dismiss (atendente leu = atendente decidiu).
 *   - Atendente pode continuar editando após dismiss.
 *
 * Defense-in-depth canonical (D-B pattern Brief 5):
 *   - Capability gate é responsabilidade do hook caller (useProtectionCheck).
 *   - Componente recebe `validation` já filtrada — render só se chamado.
 *
 * Acessibilidade:
 *   - `role="status"` (não-bloqueante; screen reader anuncia sem interromper).
 *   - `aria-live="polite"` implícito por `role=status`.
 *   - Botão dismiss tem `aria-label`.
 */
export function ProtectionWarningBanner({
  validation,
  onDismiss,
}: {
  validation: ProtectionValidation;
  onDismiss?: () => void;
}): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = (): void => {
    setDismissed(true);
    onDismiss?.();
  };

  const severityLabel = validation.severity === 'medium' ? 'Atenção' : 'Aviso';
  const categoriesPreview = validation.categories.slice(0, 2).join(' · ');

  return (
    <div
      role="status"
      data-testid="protection-warning-banner"
      className="protection-banner protection-banner--warn"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2, 8px)',
        padding: 'var(--space-2, 8px) var(--space-3, 12px)',
        fontSize: 'var(--text-xs, 12px)',
        background: 'var(--warning-bg, #fef3c7)',
        color: 'var(--warning-fg, #92400e)',
        borderTop: '1px solid var(--warning-border, #fcd34d)',
        borderBottom: '1px solid var(--warning-border, #fcd34d)',
      }}
    >
      <span aria-hidden style={{ fontSize: 14 }}>
        ⚠
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>{severityLabel} pré-envio</strong>
        <span style={{ opacity: 0.85 }}> · {validation.reason}</span>
        {categoriesPreview.length > 0 && (
          <span style={{ opacity: 0.7, marginLeft: 8 }}>({categoriesPreview})</span>
        )}
        {validation.timeoutFallback === true && (
          <span
            data-testid="protection-warning-timeout-fallback"
            style={{ opacity: 0.7, marginLeft: 8, fontStyle: 'italic' }}
          >
            · IA degradada (fallback regex)
          </span>
        )}
      </div>
      {validation.redactionSuggestion != null && validation.redactionSuggestion.length > 0 && (
        <details
          data-testid="protection-warning-suggestion-toggle"
          style={{ fontSize: '11px', opacity: 0.85 }}
        >
          <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Sugestão</summary>
          <pre
            data-testid="protection-warning-suggestion"
            style={{
              marginTop: 4,
              padding: 6,
              background: 'var(--bg-surface, #fff)',
              borderRadius: 4,
              fontFamily: 'var(--font-mono, monospace)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxWidth: 280,
            }}
          >
            {validation.redactionSuggestion}
          </pre>
        </details>
      )}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dispensar aviso"
        data-testid="protection-warning-dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--warning-fg, #92400e)',
          padding: 4,
          fontSize: 14,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
    </div>
  );
}
